import { Tooltip } from '@mantine/core'
import { Users } from 'lucide-react'

import { formatContactCount } from 'src/components/Inputs/CarrierAutocomplete/knownContacts'

/**
 * The one directory badge on an employment row or a VoE row, shown only
 * where it can change what the recruiter does next: a record with no contact
 * yet. Same rule, same words, on the employment table, the VoE tab and the
 * Verifications page.
 */

export type ContactBadgeTone = 'optedOut' | 'available' | 'neutral'

export interface ContactBadge {
  label: string
  tooltip: string
  tone: ContactBadgeTone
}

/** What the badge reads off a row; callers map their own field names. */
export interface ContactBadgeInput {
  isUnemployment?: boolean | null
  suppressedContactTypes?: readonly string[] | null
  dotNumber?: string | null
  carrierContactCount?: number | null
  contactName?: string | null
  email?: string | null
  phone?: string | null
}

/** The noun the tooltips use for the record: an employment entry or a verification. */
export type ContactBadgeSubject = 'entry' | 'verification'

const contactBadgeStyles: Record<ContactBadgeTone, string> = {
  optedOut: 'bg-amber-50 text-amber-700',
  available: 'bg-blue-50 text-blue-700',
  neutral: 'bg-gray-100 text-gray-500',
}

const SUPPRESSED_CONTACT_TYPE_ORDER = ['EMAIL', 'PHONE', 'FAX'] as const

const SUPPRESSED_CONTACT_TYPE_WORDS: Record<string, string> = {
  EMAIL: 'email',
  PHONE: 'phone',
  FAX: 'fax',
}

/** "email", "email and phone", "email, phone and fax". */
const joinWithAnd = (words: string[]): string =>
  words.length <= 1
    ? words.join('')
    : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`

const isBlank = (value?: string | null): boolean => !value?.trim()

/**
 * - Opted out wins: any suppressed contact type on the record.
 * - A record that already has a contact gets no badge. A count there read as
 *   "come and choose" on a record that is already done, and a tooltip saying
 *   otherwise is too hidden to fix that.
 * - Otherwise the count of reachable directory contacts for the DOT
 *   (`carrierContactCount`, 0 when missing): blue when there are some to use,
 *   gray "0 contacts" when there are none. With no DOT there is nothing to
 *   look up, so the row reads "0 contacts" and asks for a DOT.
 *
 * Unemployment rows get no badge.
 */
export const getContactBadge = (
  item: ContactBadgeInput,
  subject: ContactBadgeSubject = 'entry'
): ContactBadge | null => {
  if (item.isUnemployment) return null

  const suppressed = item.suppressedContactTypes ?? []
  if (suppressed.length > 0) {
    const words = SUPPRESSED_CONTACT_TYPE_ORDER.filter((type) =>
      suppressed.includes(type)
    ).map((type) => SUPPRESSED_CONTACT_TYPE_WORDS[type])
    return {
      label: 'Opted out',
      tone: 'optedOut',
      tooltip:
        words.length > 0
          ? `The contact on this ${subject} opted out of ${joinWithAnd(
              words
            )} for verifications.`
          : `The contact on this ${subject} opted out of verifications.`,
    }
  }

  const hasContact =
    !isBlank(item.contactName) || !isBlank(item.email) || !isBlank(item.phone)
  if (hasContact) return null

  const dotNumber = item.dotNumber?.trim()
  // The API reports 0 without a DOT; guard anyway so no tooltip reads "DOT ".
  const count = dotNumber ? Math.max(0, item.carrierContactCount ?? 0) : 0
  const label = formatContactCount(count)

  if (count === 0) {
    return {
      label,
      tone: 'neutral',
      tooltip: dotNumber
        ? `0 contacts for DOT ${dotNumber} yet. The contact saved on this ${subject} becomes the first.`
        : 'Add a DOT number to see contacts for this employer.',
    }
  }

  return {
    label,
    tone: 'available',
    tooltip:
      count === 1
        ? `${label} for DOT ${dotNumber}. It is not on this ${subject} yet — open it to use it.`
        : `${label} for DOT ${dotNumber}. None is on this ${subject} yet — open it to use one.`,
  }
}

const ContactCountBadge = ({
  item,
  subject = 'entry',
  hideWhenNone = false,
}: {
  item: ContactBadgeInput
  subject?: ContactBadgeSubject
  /**
   * Skip the gray "0 contacts" badge. For tables where it says nothing a
   * recruiter can act on; the badge still shows a count above 0 or "Opted out".
   */
  hideWhenNone?: boolean
}) => {
  const badge = getContactBadge(item, subject)
  if (!badge) return null
  if (hideWhenNone && badge.tone === 'neutral') return null

  return (
    <Tooltip label={badge.tooltip} position="top" withArrow>
      <span
        data-testid="contact-count-badge"
        className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${
          contactBadgeStyles[badge.tone]
        }`}
      >
        {badge.tone === 'available' && <Users size={11} aria-hidden />}
        {badge.label}
      </span>
    </Tooltip>
  )
}

export default ContactCountBadge
